import { useRecoilValue } from 'recoil';
import { Pharmacy } from '@/apis/pharmacy/interface';
import { pharmacyState } from '@/recoil/pharmacy';
import { useState } from 'react';
import EmergencyMessageForm from '@/pages/OrderDetailPage/containers/EmergencyMessageForm';
import useEmergencyMessageReason from '@/hooks/etc/useEmergencyMessageReason';
import useSendEmergencyMessage from '@/hooks/etc/useSendEmergencyMessage';
import {
  EmergencyMessageReason_getEmergencyMessageReason,
  EmergencyMessageReasonType
} from '@/api/bff_types';

export interface EmergencyMessageFormViewModel {
  form: EmergencyMessageForm;
  options: Array<{ value: string; label: string }>;
  initialOption?: { value: string; label: string };
  manualReasonSelected: () => boolean;
  changeReasonId: (reasonId: string) => void;
  inputReason: (reason: string) => void;
  clearReason: () => void;
  inputPharmacyPhone: (input: string) => void;
  submit: () => Promise<void>;
}

interface EmergencyMessageForm {
  reasonId?: string;
  reason?: string;
  pharmacyPhone?: string;
}

interface UseEmergencyFormViewModelProps {
  orderId: number;
}

const optionToSelect = (
  option: EmergencyMessageReason_getEmergencyMessageReason
) => {
  return {
    value: option.id,
    label: option.reason
  };
};
export const useEmergencyFormViewModel = (
  props: UseEmergencyFormViewModelProps
): EmergencyMessageFormViewModel => {
  const pharmacy = useRecoilValue<Pharmacy>(pharmacyState);
  const { data: options } = useEmergencyMessageReason();
  const { mutateAsync } = useSendEmergencyMessage();
  const [form, setForm] = useState<EmergencyMessageForm>({
    reasonId: options?.[0].id,
    reason: options?.[0].reason,
    pharmacyPhone: pharmacy.pharmacy?.tel
  });

  const manualReasonSelected = () => {
    const selectedReason = options?.find(item => item.id === form.reasonId);
    return Boolean(
      selectedReason?.type === EmergencyMessageReasonType.DIRECT_INPUT
    );
  };

  const changeReasonId = (reasonId: string) => {
    const option = options?.find(item => item.id === reasonId);

    if (!option) {
      throw new Error('옵션이 잘못되었습니다.');
    }

    setForm(form => ({
      ...form,
      reasonId: option.id,
      reason:
        option.type === EmergencyMessageReasonType.DIRECT_INPUT
          ? undefined
          : option.reason
    }));
  };

  const inputReason = (input: string) => {
    setForm(form => ({
      ...form,
      reason: input
    }));
  };

  const clearReason = () => {
    setForm(form => ({
      ...form,
      reason: undefined
    }));
  };

  const inputPharmacyPhone = (input: string) => {
    const numberOnlyInput = input.replace(/\D/g, '');
    setForm(form => ({
      ...form,
      pharmacyPhone: numberOnlyInput
    }));
  };

  const submit = async () => {
    if (!form.pharmacyPhone || !form.reason || !form.reasonId) {
      throw new Error('입력 값을 확인해주세요.');
    }

    await mutateAsync({
      reason: form.reason,
      reasonId: form.reasonId,
      pharmacyPhone: form.pharmacyPhone,
      orderId: `${props.orderId}`
    });
  };

  return {
    options: options?.map(optionToSelect) ?? [],
    form,
    manualReasonSelected,
    changeReasonId,
    inputReason,
    clearReason,
    inputPharmacyPhone,
    submit
  };
};
